import cx from 'classnames'
import {
  ChangeEvent,
  ComponentType,
  InputHTMLAttributes,
  SVGProps
} from 'react'
import { useFormContext } from 'react-hook-form'

import { OptionItem } from '@js/common/types/OptionItem'

import Icon from '../Icon/Icon'
import { FormErrorMessage } from './ErrorMessage'

interface SelectProps<Value extends string> {
  name: string
  className?: string
  disabled?: boolean
  placeholder?: string
  inputProps?: InputHTMLAttributes<HTMLSelectElement> & { 'data-sel'?: string }
  labelProps?: InputHTMLAttributes<HTMLLabelElement>
  options: OptionItem<Value>[]
  SelectedOptionIcon?: ComponentType<SVGProps<SVGSVGElement> & { value: Value }>
}

export function Select<Value extends string = string>({
  name,
  className,
  disabled,
  placeholder,
  inputProps,
  labelProps,
  options,
  SelectedOptionIcon
}: SelectProps<Value>) {
  const { register, formState, getFieldState, watch } = useFormContext()
  const { isValidating } = formState
  const { error, isTouched } = getFieldState(name, formState)

  const realTimeValue = watch(name)
  const displayError = error?.message as string

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { selectedIndex } = event.target.options
    const { label } = event.target.options[selectedIndex]

    window._wt({
      category: 'select',
      action: 'change',
      values: [{ type: 'text', value: label }]
    })
  }

  const selectedValue = options.find(option => option.value === realTimeValue)
  return (
    <>
      <label
        className={cx(
          '[ f-overflow f-d-bl f-pos-rel f-c-fo-pri f-p-h-020 ] FormText-wrapper f-d-bl',
          displayError ? 'f-c-bo-war f-m-b-010' : 'f-m-b-020',
          disabled && 'FormText--disabled',
          isValidating && 'is-validating',
          isTouched && !displayError && 'f-c-bo-suc400',
          SelectedOptionIcon && realTimeValue && 'has-icon',
          className
        )}
        {...labelProps}>
        {SelectedOptionIcon && realTimeValue && (
          <SelectedOptionIcon className='FormText-icon' value={realTimeValue} />
        )}
        {selectedValue?.value && (
          <span
            style={{ bottom: '12px' }}
            className={cx(
              'f-pos-ab FormSelect-display has-value',
              placeholder && 'f-p-t-005',
              SelectedOptionIcon && realTimeValue && 'has-icon'
            )}>
            {selectedValue?.label}
          </span>
        )}
        {placeholder && (
          <span
            className={`[ f-pos-ab f-c-fo-mut f-t-body ] FormTextLabel ${
              selectedValue ? 'has-value' : ''
            }`}>
            {placeholder}
          </span>
        )}
        <select
          className='FormSelect'
          {...register(name, {
            onChange: handleSelectChange
          })}
          {...inputProps}>
          {options.map((option, index: number) => {
            return (
              <option
                key={`${index}_${option.value}`}
                value={option.value}
                hidden={option.hidden}>
                {option.label}
              </option>
            )
          })}
        </select>
        <Icon
          icon='down-link-16px'
          className='[ f-icon-020 f-c-st-ter ] FormSelect-caret'
        />
      </label>
      {displayError && <FormErrorMessage>{displayError}</FormErrorMessage>}
    </>
  )
}
